<template>
  <div class="overlay" @click="$emit('close')">
    <div class="pm-dialog" @click.stop>
      <div class="pm-head">
        <h3 class="pm-title">Nueva incidencia / requerimiento</h3>
        <button class="icon-btn" @click="$emit('close')">
          <PortalIcon name="X" :size="20" />
        </button>
      </div>

      <div class="form">
        <label class="field">
          <span class="field-label">Cliente / Cuenta</span>
          <select v-model="f.cliente" class="inp" :disabled="cuentasLoading">
            <option value="">
              {{ cuentasLoading ? "Cargando clientes…" : "— Selecciona un cliente —" }}
            </option>
            <option v-for="c in cuentas" :key="c.idcuenta || c.nombre" :value="c.nombre">
              {{ c.nombre }}
            </option>
          </select>
          <span v-if="cuentasError" class="field-error">{{ cuentasError }}</span>
        </label>

        <label class="field">
          <span class="field-label">Asunto</span>
          <textarea
            v-model="f.asunto"
            rows="2"
            placeholder="Qué solicitó el cliente"
            class="inp textarea"
          ></textarea>
        </label>

        <div class="grid-2">
          <label class="field">
            <span class="field-label">Área Responsable</span>
            <select v-model="f.area" class="inp">
              <option v-for="o in AREA_LIST" :key="o">{{ o }}</option>
            </select>
          </label>
          <label class="field">
            <span class="field-label">Categoría</span>
            <select v-model="f.categoria" class="inp" :disabled="catLoading">
              <option v-for="o in categoriaList" :key="o">{{ o }}</option>
            </select>
          </label>
          <label class="field">
            <span class="field-label">Prioridad</span>
            <select v-model="f.prioridad" class="inp" :disabled="catLoading">
              <option v-for="o in prioridadList" :key="o">{{ o }}</option>
            </select>
          </label>
          <label class="field">
            <span class="field-label">Canal</span>
            <select v-model="f.canal" class="inp">
              <option v-for="o in CANALES" :key="o">{{ o }}</option>
            </select>
          </label>
          <label class="field">
            <span class="field-label">Estatus</span>
            <select v-model="f.estatus" class="inp" :disabled="catLoading">
              <option v-for="o in estatusList" :key="o">{{ o }}</option>
            </select>
          </label>
          <label class="field">
            <span class="field-label">Fecha compromiso</span>
            <input v-model="f.fechaCompromiso" type="date" class="inp" />
          </label>
          <label class="field">
            <span class="field-label">Solicitante</span>
            <input v-model="f.solicitante" class="inp" placeholder="Nombre" />
          </label>
          <label class="field">
            <span class="field-label">Tipo</span>
            <select v-model="f.solicitanteTipo" class="inp">
              <option>Externo</option>
              <option>Interno</option>
            </select>
          </label>
        </div>

        <label class="field">
          <span class="field-label">Comentarios</span>
          <textarea
            v-model="f.comentarios"
            rows="2"
            placeholder="Comentarios (opcional)"
            class="inp textarea"
          ></textarea>
        </label>

        <p v-if="error" class="form-error">{{ error }}</p>

        <button
          class="submit"
          :disabled="!valido || busy"
          :style="{ background: valido && !busy ? '#003ca5' : '#cbd5e1' }"
          @click="registrar"
        >
          {{ busy ? "Creando…" : "Registrar" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import PortalIcon from "./PortalIcon.vue";
import portalService from "../../../services/portal.service";
import {
  AREA_LIST,
  CATEGORIAS,
  PRIORIDADES,
  CANALES,
  ESTATUS,
} from "./portal.constants";

export default {
  name: "NuevoRegistroModal",
  components: { PortalIcon },
  props: {
    // Estado de creación gestionado por el padre (RegistrosTab).
    busy: { type: Boolean, default: false },
    error: { type: String, default: "" },
  },
  emits: ["close", "crear"],
  data() {
    return {
      AREA_LIST,
      CANALES,
      // Listas de catálogo: inician con constantes (fallback) y se reemplazan
      // con lo que devuelve el servicio /estatus al montar.
      estatusList: [...ESTATUS],
      prioridadList: [...PRIORIDADES],
      categoriaList: [...CATEGORIAS],
      catLoading: false,
      catError: "",
      cuentas: [],
      cuentasLoading: false,
      cuentasError: "",
      f: {
        cliente: "",
        asunto: "",
        area: AREA_LIST[0],
        categoria: "Requerimiento",
        prioridad: "Medio",
        canal: "Mail",
        estatus: "En proceso",
        solicitante: "",
        solicitanteTipo: "Externo",
        fechaCompromiso: "",
        comentarios: "",
      },
    };
  },
  computed: {
    valido() {
      return this.f.cliente.trim() && this.f.asunto.trim();
    },
  },
  async mounted() {
    // Carga en paralelo clientes y catálogos (estatus/prioridad/categoría).
    await Promise.all([this.cargarCuentas(), this.cargarCatalogos()]);
  },
  methods: {
    async cargarCuentas() {
      this.cuentasLoading = true;
      this.cuentasError = "";
      try {
        this.cuentas = await portalService.fetchCuentas();
      } catch (e) {
        this.cuentasError = "No se pudieron cargar los clientes.";
      } finally {
        this.cuentasLoading = false;
      }
    },
    async cargarCatalogos() {
      this.catLoading = true;
      this.catError = "";
      try {
        const c = await portalService.fetchCatalogos();
        if (c.estatus.length) this.estatusList = c.estatus;
        if (c.prioridades.length) this.prioridadList = c.prioridades;
        if (c.categorias.length) this.categoriaList = c.categorias;
        // Asegura que los valores del formulario existan en el catálogo.
        this.f.estatus = this.pick(this.estatusList, this.f.estatus, "En proceso");
        this.f.prioridad = this.pick(this.prioridadList, this.f.prioridad, "Media");
        this.f.categoria = this.pick(
          this.categoriaList,
          this.f.categoria,
          "Requerimiento"
        );
      } catch (e) {
        this.catError = "No se pudieron cargar los catálogos.";
      } finally {
        this.catLoading = false;
      }
    },
    pick(list, current, preferred) {
      if (list.includes(current)) return current;
      if (list.includes(preferred)) return preferred;
      return list[0] || current;
    },
    registrar() {
      if (!this.valido || this.busy) return;
      this.$emit("crear", {
        ...this.f,
        fechaCompromiso: this.f.fechaCompromiso || null,
      });
    },
  },
};
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.45);
}
.pm-dialog {
  width: 100%;
  max-width: 512px;
  max-height: 100%;
  overflow-y: auto;
  background: var(--pt-card);
  border-radius: 16px;
  padding: 20px;
}
.pm-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.pm-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0;
}
.icon-btn {
  background: none;
  border: 0;
  cursor: pointer;
  color: var(--pt-muted);
}
.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.field {
  display: block;
}
.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--pt-dim);
  margin-bottom: 4px;
}
.inp {
  width: 100%;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  border: 1px solid var(--pt-border);
  outline: none;
  background: var(--pt-card);
  color: var(--pt-text);
  box-sizing: border-box;
  font-family: inherit;
}
.inp:focus {
  border-color: #003ca5;
}
.textarea {
  resize: none;
}
.form-error {
  margin: 0;
  font-size: 13px;
  color: #b91c1c;
}
.field-error {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #b91c1c;
}
.submit {
  width: 100%;
  padding: 10px;
  border: 0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  margin-top: 4px;
}
.submit:disabled {
  cursor: not-allowed;
}
</style>
